import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import ChartCard from "../ChartCard";
import { getPieComparisons, isLessThanOne } from "../../../utils/helpers";

// Helper to normalize raw data
function normalizeData(rawData) {
    if (Array.isArray(rawData[0])) {
        const [org = [], peers = [], clients = []] = rawData;
        return org.map((d, i) => ({
            // groupLabel: d.label ?? d.gender,
            groupLabel: d.gender,
            Org: d.value,
            Industrypeers: peers[i]?.value ?? 0,
            unumclient: clients[i]?.value ?? 0,
            count: d.count,
        }));
    }
    return rawData;
}

const CustomTooltip = ({ count }) => {
    return (
        <div className="dashboard-tooltip">
            <label>Total count: {count}</label>
            <div className="tooltip-Arrow"></div>
        </div>
    );
};

CustomTooltip.propTypes = {
    count: PropTypes.number,
};

// Helper to check if data has valid numbers
function hasValidNumbers(data) {
    return (
        Array.isArray(data) &&
        data.some(
            (item) =>
                item &&
                typeof item.groupLabel === "string" &&
                [item.Org, item.Industrypeers, item.unumclient].some(
                    (v) => typeof v === "number" && !isNaN(v)
                )
        )
    );
}

// Helper to get display label
const displayLabelMap = {
    Org: (orgName) => orgName,
    IndustryPeers: () => "Industry Peers",
    unumclient: () => "Unum Clients",
};

const getCompText = (selected) => {
    if (selected?.length === 2) return "All";
    if (selected?.length === 1) return selected[0]?.value;
    return "Organization Only";
};

const getChartData = (data, key) =>
    data.map((d) => {
        const matchingKey = Object.keys(d).find(
            (k) => k.toLowerCase() === key.toLowerCase()
        );
        return {
            id: d.groupLabel,
            value: d[matchingKey] ?? 0,
            count: d.count,
        };
    });

// Display values in the range (0, 1) as "<1"; show 0 as "0"
const formatPieDisplayValue = (value) => (isLessThanOne(value) ? "<1" : value);

// ArcLinkLabel for dashboard
const DashboardArcLinkLabel = ({ id, value }) => (
    <>
        <tspan x="0" dy="0" className="charts-inner-text">
            {id}
        </tspan>
        <tspan
            x={id === "Female" ? "0.8em" : "0"}
            dy="1.2em"
            className="charts-inner-text"
        >
            {formatPieDisplayValue(value)}%
        </tspan>
    </>
);

DashboardArcLinkLabel.propTypes = {
    id: PropTypes.string,
    value: PropTypes.number,
};

// ArcLinkLabel for disability snapshot
const DisabilityArcLinkLabel = ({ id, value }) => (
    <>
        <tspan x={"0em"} dy={"0em"} className="charts-inner-text">
            {formatPieDisplayValue(value)}%
        </tspan>
    </>
);

DisabilityArcLinkLabel.propTypes = {
    id: PropTypes.string,
    value: PropTypes.number,
};

// Extracted inline functions — NO logic removed

const dashboardArcLinkLabelFn = (d) => (
    <DashboardArcLinkLabel id={d.id} value={d.value} />
);

const disabilityArcLinkLabelFn = (d) => (
    <DisabilityArcLinkLabel id={d.id} value={d.value} />
);

const disabilityTooltipFn = (d) => <CustomTooltip count={d.datum.data.count} />;

const createSymbolShapeFn = (comparisons) => {
    const SymbolShape = ({ y, size, fill, id }) => {
        const stroke =
            comparisons.find((c) => c.key === id)?.borderColor || "#015294";

        return (
            <rect
                x={2}
                y={y}
                width={size}
                height={size}
                rx={3}
                ry={3}
                fill={fill}
                stroke={stroke}
                strokeWidth={1}
            />
        );
    };

    SymbolShape.propTypes = {
        y: PropTypes.number,
        size: PropTypes.number,
        fill: PropTypes.string,
        id: PropTypes.string,
    };

    return SymbolShape;
};

const ReusableDonutChart = ({
    title,
    description,
    accessibilityText,
    data: rawData = [],
    organizationName,
    selectedCompanyYourCompanyTo,
    disabilitySnapshot,
    errorMessage,
    totalRecords,
    viewId,
    tableData,
    reportDetails,
    disclaimer,
    cacheKey,
    clientClaimCount,
}) => {
    const data = useMemo(() => normalizeData(rawData), [rawData]);

    const compText = useMemo(
        () => getCompText(selectedCompanyYourCompanyTo),
        [selectedCompanyYourCompanyTo]
    );

    const comparisonMap = useMemo(
        () => getPieComparisons?.(organizationName) ?? {},
        [organizationName]
    );

    const comparisons = comparisonMap[compText] || [];

    const keysToUse =
        comparisons.length > 0 ? comparisons.map((c) => c.key) : ["Org"];

    const buildPieOption = useCallback(
        (key, baseConfig, cmp, chartData) => ({
            ...baseConfig,
            data: chartData,
            colors: cmp.pieColors,
            borderColor: cmp.borderColor || baseConfig.borderColor,
        }),
        []
    );

    // ------------------------
    // Dashboard Pie Options
    // ------------------------
    const dashboardPieOptions = useMemo(() => {
        return keysToUse.map((key) => {
            const cmp = comparisons.find((c) => c.key === key) || {};
            const chartData = getChartData(data, key);

            // ORIGINAL base object — unchanged
            const base = {
                margin: { top: 75, right: 75, bottom: 75, left: 70 },
                innerRadius: 0.75,
                padAngle: 2.5,
                cornerRadius: 3,
                borderWidth: 1,
                borderColor: cmp.borderColor,
                enableArcLabels: false,
                isInteractive: false,

                // extracted (no logic change)
                arcLinkLabel: dashboardArcLinkLabelFn,

                enableArcLinkLabels: true,
                arcLinkLabelsSkipAngle: 0,
                arcLinkLabelsTextColor: "#525252",
                arcLinkLabelsThickness: 1,
                arcLinkLabelsColor: "#015294",
                arcLinkLabelsOffset: 0,
                arcLinkLabelsDiagonalLength: 15,
                arcLinkLabelsStraightLength: 0,
                animate: true,
                motionConfig: "gentle",
            };

            return buildPieOption(key, base, cmp, chartData);
        });
    }, [data, keysToUse, comparisons, buildPieOption]);

    // ------------------------
    // Disability Snapshot Options
    // ------------------------
    const disabilitySnapshotPieOptions = useMemo(() => {
        const symbolShape = createSymbolShapeFn(comparisons);

        return keysToUse.map((key) => {
            const cmp = comparisons.find((c) => c.key === key) || {};
            const chartData = getChartData(data, key);

            // ORIGINAL base object — unchanged
            const base = {
                margin: { top: 73, right: 210, bottom: 113, left: 14 },
                innerRadius: 0.75,
                padAngle: 2.5,
                cornerRadius: 3,
                startAngle: -45,
                borderWidth: 2,
                borderColor: cmp.borderColor || "#015294",
                enableArcLabels: false,
                isInteractive: true,
                // extracted (no logic change)
                tooltip: disabilityTooltipFn,
                // extracted

                arcLinkLabel: disabilityArcLinkLabelFn,

                enableArcLinkLabels: true,
                arcLinkLabelsSkipAngle: 0,
                arcLinkLabelsTextColor: "#525252",
                arcLinkLabelsThickness: 1,
                arcLinkLabelsColor: "#015294",
                arcLinkLabelsOffset: 0,
                arcLinkLabelsDiagonalLength: 13,
                arcLinkLabelsStraightLength: 0,
                animate: true,
                motionConfig: "gentle",
                legends: [
                    {
                        anchor: "right",
                        direction: "column",
                        translateX: 108,
                        translateY: 0,
                        itemWidth: 95,
                        itemHeight: 34,
                        symbolSize: 15,

                        // extracted
                        symbolShape: symbolShape,
                    },
                ],
            };

            return buildPieOption(key, base, cmp, chartData);
        });
    }, [data, keysToUse, comparisons, buildPieOption]);

    const labels = useMemo(
        () =>
            keysToUse.map((key) =>
                displayLabelMap[key]
                    ? displayLabelMap[key](organizationName)
                    : key
            ),
        [keysToUse, organizationName]
    );

    if (!hasValidNumbers(data) && disabilitySnapshot !== true) {
        return (
            <ChartCard
                title={title}
                description={description}
                accessibilityText={accessibilityText}
                variant="donut"
                chartOptions={[]}
                labels={[]}
                selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
                errorMessage={errorMessage}
                disabilitySnapshot={disabilitySnapshot}
                viewId={viewId}
                tableData={tableData}
                reportDetails={reportDetails}
                disclaimer={disclaimer}
                clientClaimCount={clientClaimCount}
            />
        );
    }

    return (
        <ChartCard
            title={title}
            description={description}
            accessibilityText={accessibilityText}
            variant="donut"
            chartOptions={
                disabilitySnapshot
                    ? disabilitySnapshotPieOptions
                    : dashboardPieOptions
            }
            labels={labels}
            selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
            errorMessage={errorMessage}
            disabilitySnapshot={disabilitySnapshot}
            viewId={viewId}
            pieTotalRecords={totalRecords}
            tableData={tableData}
            reportDetails={reportDetails}
            disclaimer={disclaimer}
            cacheKey={cacheKey}
            clientClaimCount={clientClaimCount}
        />
    );
};

ReusableDonutChart.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    accessibilityText: PropTypes.string,
    organizationName: PropTypes.string,
    selectedCompanyYourCompanyTo: PropTypes.array,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            groupLabel: PropTypes.string,
            Org: PropTypes.number,
            Industrypeers: PropTypes.number,
            unumclient: PropTypes.number,
        })
    ).isRequired,
    disabilitySnapshot: PropTypes.bool,
    errorMessage: PropTypes.string,
    viewId: PropTypes.string,
    totalRecords: PropTypes.number,
    disclaimer: PropTypes.string,
    clientClaimCount: PropTypes.number,
};

export default ReusableDonutChart;
